import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarRange, Clock } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LABEL = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday' };

export default function MyTimetablePage() {
  const [data, setData] = useState({ sessions: [], total_weekly_hours: 0, all_periods: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.ttMyTimetable();
        setData(res.data || { sessions: [], total_weekly_hours: 0, all_periods: [] });
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const cellFor = (day, periodId) => data.sessions.find(s => s.day === day && s.period_id === periodId);
  const periods = data.all_periods || [];

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="my-timetable-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-600">My Schedule</p>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>My Timetable</h1>
          <p className="text-sm text-slate-500 mt-1">Your published weekly class schedule, across all batches you teach.</p>
        </div>
        <Card className="border-violet-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-violet-600" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-500">Weekly Hours</p>
              <p className="text-2xl font-black text-violet-700" data-testid="my-weekly-hours">{data.total_weekly_hours}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-12">Loading...</p>
      ) : data.sessions.length === 0 ? (
        <div className="text-center py-16" data-testid="my-timetable-empty">
          <CalendarRange className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No published timetable assigned to you yet.</p>
        </div>
      ) : periods.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-12">No session slots configured yet.</p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="my-timetable-grid">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-2.5 text-left font-bold sticky left-0 bg-slate-900 min-w-[110px]">Session</th>
                {DAYS.map(d => <th key={d} className="p-2.5 text-center font-bold min-w-[150px]">{DAY_LABEL[d]}</th>)}
              </tr>
            </thead>
            <tbody>
              {periods.map(p => (
                <tr key={p._id} className={`border-t border-slate-100 ${p.is_break ? 'bg-amber-50' : ''}`} data-testid={`my-tt-row-${p._id}`}>
                  <td className="p-2 align-top sticky left-0 bg-white border-r border-slate-100">
                    <div className="font-bold text-slate-700">{p.label}</div>
                    <div className="text-[10px] text-slate-400">{p.start_time}–{p.end_time}</div>
                  </td>
                  {p.is_break ? (
                    <td colSpan={DAYS.length} className="p-2 text-center text-xs font-bold text-amber-700" data-testid={`my-tt-break-row-${p._id}`}>
                      {p.break_label || 'LUNCH'}
                    </td>
                  ) : DAYS.map(d => {
                    const s = cellFor(d, p._id);
                    return (
                      <td key={d} className="p-2 align-top text-center" data-testid={`my-tt-cell-${d}-${p._id}`}>
                        {s ? (
                          <div className="rounded-md bg-violet-50 border border-violet-200 px-2 py-1.5">
                            <div className="font-bold text-violet-700">{s.subject || '—'}</div>
                            <div className="text-[10px] text-slate-500">{s.batch}{s.room ? ` · ${s.room}` : ''}</div>
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
